import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Printer,
    Check,
    Calendar,
    Clock,
    Layers,
    DollarSign,
    Sparkles,
    Mail,
    Globe2,
    TrendingUp,
    Download,
    Megaphone,
} from "lucide-react";

/* Brand-facing pilot proposal page.
   - Public URL (you DM the link)
   - Looks like a serious B2B product page, not a pitch deck
   - Print-to-PDF compatible (clean print styles)
   - Links into the live product so they can click through */

const VALUE_PROPS = [
    {
        Icon: Layers,
        title: "Instantly license creator content",
        body: "No contracts. No back-and-forth. Get clean rights to your best videos in one click.",
        tone: "coral",
    },
    {
        Icon: Globe2,
        title: "Run it everywhere",
        body: "Total control across campaigns and channels — run everything from your own ad accounts, your edits, your channels, and turn one winning video into multi-channel revenue.",
        tone: "lime",
    },
    {
        Icon: TrendingUp,
        title: "Pay only on performance",
        body: "Only pay when content drives revenue. Creators earn a % of what their videos actually generate.",
        tone: "coral",
    },
];

const STEPS = [
    {
        n: "01",
        when: "Day 1",
        title: "30-min onboarding call",
        body: "We walk through your creator list and align on payout terms — bulk default, per-creator or per-video, flat fee or rev-share.",
    },
    {
        n: "02",
        when: "Days 2–3",
        title: "We bring in your creators",
        body: "They upload their videos. You get clean rights instantly — no contracts, no back-and-forth.",
    },
    {
        n: "03",
        when: "Days 4–30",
        title: "You run it. We track what it drives.",
        body: "Download the raw videos and run them natively on Meta, Google, Amazon — your way. Castline tracks spend, revenue and ROAS in real time.",
    },
    {
        n: "04",
        when: "Day 30",
        title: "Pay creators on performance",
        body: "Fund the creator's payout with one click. Real Stripe, real money, only based on the revenue the videos actually drove.",
    },
];

const YOU_GIVE = [
    "30 minutes for an onboarding call",
    "Your creator list — we'll bring them in",
    "Read access to Meta / Google ad numbers (or weekly export — your call)",
    "A creator-payout commitment — your terms, your call",
];
const YOU_GET = [
    "Castline platform — free for the entire pilot",
    "1 month of per-channel attribution + ROAS dashboard",
    "Raw .mp4 downloads + UTM, promo code & campaign tag per video",
    "1-page weekly performance recap, by email",
    "Right of first refusal on platform pricing if you stay after the pilot",
];

// --- Inline dashboard-preview component (visual break for pitch) ---------
const PREVIEW_VIDEOS = [
    {
        img: "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600&q=60",
        title: "5-step glass skin demo",
        creator: "Maya Chen",
        channels: ["Meta", "Google"],
    },
    {
        img: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=600&q=60",
        title: "Hoodie unbox · soft cinematic",
        creator: "Leo Park",
        channels: ["Meta", "YouTube"],
    },
    {
        img: "https://images.unsplash.com/photo-1594381898411-846e7d193883?w=600&q=60",
        title: "Protein shake · post-workout",
        creator: "Riya Kapoor",
        channels: ["Meta", "Amazon"],
    },
];

function DashboardPreview() {
    return (
        <div
            className="relative rounded-3xl overflow-hidden bg-slate-900 p-5 md:p-8 text-white"
            data-testid="dashboard-preview"
        >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl bg-coral/30" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full blur-3xl bg-lime/20" />
            <div className="relative">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-sm">
                            c
                        </div>
                        <span className="font-display text-sm font-semibold">Your creator library</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-300">
                        <span className="px-2 py-0.5 rounded-full bg-white/10">All creators · 16</span>
                        <span className="px-2 py-0.5 rounded-full bg-white/10">All channels</span>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {PREVIEW_VIDEOS.map((v) => (
                        <div
                            key={v.title}
                            className="rounded-xl overflow-hidden bg-white text-slate-900 shadow-lg"
                        >
                            <div className="aspect-video relative">
                                <img
                                    src={v.img}
                                    alt={v.title}
                                    className="w-full h-full object-cover"
                                />
                                <span
                                    className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
                                    style={{ background: "#A3E635", color: "#365314" }}
                                >
                                    {v.creator}
                                </span>
                            </div>
                            <div className="p-3">
                                <div className="font-display text-sm font-semibold truncate">
                                    {v.title}
                                </div>
                                <div className="flex items-center justify-between mt-2 gap-2">
                                    <div className="flex gap-1 flex-wrap">
                                        {v.channels.map((c) => (
                                            <span
                                                key={c}
                                                className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
                                            >
                                                {c}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-1 text-[11px] text-coral font-semibold">
                                        <Download className="w-3 h-3" /> Raw
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {/* ROAS strip */}
                <div className="mt-5 grid grid-cols-3 gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Tracked spend</div>
                        <div className="font-display text-xl font-semibold">$42,180</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Attributed revenue</div>
                        <div className="font-display text-xl font-semibold" style={{ color: "#A3E635" }}>$168,720</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Blended ROAS</div>
                        <div className="font-display text-xl font-semibold">4.00×</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function AttributionBar() {
    const COMMISSION_PCT = 10; // illustrative default rev-share used for pitch copy
    const rows = [
        {
            creator: "Maya Chen",
            channels: { meta: 18400, google: 11200, amazon: 6400, youtube: 2100 },
        },
        {
            creator: "Leo Park",
            channels: { meta: 24100, google: 14300, amazon: 3200, youtube: 6800 },
        },
        {
            creator: "Riya Kapoor",
            channels: { meta: 21600, google: 9800, amazon: 9100, youtube: 4200 },
        },
    ];
    const channelMeta = {
        meta: { label: "Meta", color: "#FF5A5F" },
        google: { label: "Google", color: "#A3E635" },
        amazon: { label: "Amazon", color: "#0f172a" },
        youtube: { label: "YouTube", color: "#64748b" },
    };
    const max = Math.max(
        ...rows.flatMap((r) => Object.values(r.channels)),
    );

    return (
        <div
            className="rounded-3xl bg-white border hairline p-6 md:p-8"
            data-testid="attribution-illustration"
        >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div className="overline text-coral">PER-CREATOR · PER-CHANNEL ATTRIBUTION</div>
                <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
                    {Object.entries(channelMeta).map(([k, v]) => (
                        <div key={k} className="inline-flex items-center gap-1.5">
                            <span
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ background: v.color }}
                            />
                            {v.label}
                        </div>
                    ))}
                </div>
            </div>
            <div className="font-display text-xl font-semibold mb-5">
                See which creator drives revenue — on which channel.
            </div>
            <div className="space-y-5">
                {rows.map((r) => {
                    const total = Object.values(r.channels).reduce(
                        (a, b) => a + b,
                        0,
                    );
                    const commission = Math.round(total * (COMMISSION_PCT / 100));
                    return (
                        <div key={r.creator} data-testid={`attr-row-${r.creator}`}>
                            <div className="flex items-baseline justify-between mb-1.5 flex-wrap gap-2">
                                <div className="font-display text-sm font-semibold text-slate-900">
                                    {r.creator}
                                </div>
                                <div className="flex items-baseline gap-5 text-xs text-slate-500">
                                    <span>
                                        revenue{" "}
                                        <span className="font-display font-semibold text-slate-900">
                                            ${total.toLocaleString()}
                                        </span>
                                    </span>
                                    <span>
                                        commission{" "}
                                        <span
                                            className="font-display font-semibold"
                                            style={{ color: "#FF5A5F" }}
                                        >
                                            ${commission.toLocaleString()}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            {/* Grouped bars */}
                            <div className="space-y-1.5">
                                {Object.entries(r.channels).map(([ch, val]) => {
                                    const meta = channelMeta[ch];
                                    const pct = (val / max) * 100;
                                    return (
                                        <div
                                            key={ch}
                                            className="flex items-center gap-3"
                                        >
                                            <div className="w-16 text-[11px] text-slate-500">
                                                {meta.label}
                                            </div>
                                            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{
                                                        width: `${pct}%`,
                                                        background: meta.color,
                                                    }}
                                                />
                                            </div>
                                            <div className="w-20 text-right text-[11px] font-display font-semibold">
                                                ${val.toLocaleString()}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="mt-6 pt-5 border-t hairline text-xs text-slate-500 leading-relaxed">
                One dashboard — not a merged line. Every dollar of attributed revenue is
                traced back to the creator and the channel that drove it.
                Commission shown at an illustrative <span className="text-slate-900 font-medium">{COMMISSION_PCT}% rev-share</span> — your terms, your call.
            </div>
        </div>
    );
}

export default function PilotPage() {
    return (
        <div className="min-h-screen bg-white text-slate-900 pilot-page">
            {/* HEADER STRIP */}
            <header className="border-b hairline bg-white sticky top-0 z-40 print:hidden">
                <div className="max-w-[920px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="pilot-home">
                        <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-sm">
                            c
                        </div>
                        <span className="font-display text-lg font-semibold">castline</span>
                        <span className="text-slate-300 mx-1">·</span>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Founding pilot</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => window.print()}
                            className="btn-pill-ghost inline-flex items-center text-sm"
                            data-testid="pilot-print"
                        >
                            <Printer className="w-4 h-4 mr-2" /> PDF
                        </button>
                        <a
                            href="mailto:hello@joincastline.com?subject=Yes%2C%20count%20us%20in%20for%20the%20Castline%20pilot&body=Hi%20Castline%20team%2C%0A%0AWe%27re%20interested%20in%20joining%20the%20founding%20pilot.%20%20%0A%0ABrand%20name%3A%20%5Byour%20brand%5D%0AContact%20name%3A%20%5Byour%20name%5D%0AWebsite%3A%20%0A%0AHappy%20to%20jump%20on%20a%20call.%0A%0AThanks%21"
                            className="btn-pill-primary inline-flex items-center text-sm"
                            data-testid="pilot-cta-header"
                        >
                            Start free pilot <ArrowRight className="w-4 h-4 ml-2" />
                        </a>
                    </div>
                </div>
            </header>

            <main className="max-w-[920px] mx-auto px-5 lg:px-8 py-12 lg:py-16 space-y-16">
                {/* HERO */}
                <section data-testid="pilot-hero">
                    <div className="chip chip-coral inline-flex mb-5">
                        <Sparkles className="w-3 h-3 mr-1.5" /> FOUNDING PILOT · 3 BRANDS ONLY
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] mb-6 tracking-tight">
                        Take your{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10">winning videos</span>
                            <span className="absolute inset-x-0 bottom-1 h-3 bg-lime/70 -z-0 rounded-sm" />
                        </span>
                        , license them instantly, and turn them into{" "}
                        <span className="text-coral">revenue everywhere</span>.
                    </h1>
                    <p className="text-slate-600 text-lg leading-relaxed max-w-2xl">
                        Your best content is stuck on one platform. Run it across every channel and drive more sales — no new production needed. You set the terms and only pay creators on the revenue they drive.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3 print:hidden">
                        <a
                            href="mailto:hello@joincastline.com?subject=Yes%2C%20count%20us%20in%20for%20the%20Castline%20pilot&body=Hi%20Castline%20team%2C%0A%0AWe%27re%20interested%20in%20joining%20the%20founding%20pilot.%20%20%0A%0ABrand%20name%3A%20%5Byour%20brand%5D%0AContact%20name%3A%20%5Byour%20name%5D%0AWebsite%3A%20%0A%0AHappy%20to%20jump%20on%20a%20call.%0A%0AThanks%21"
                            className="btn-pill-primary inline-flex items-center"
                            data-testid="pilot-cta-yes"
                        >
                            Start free pilot <ArrowRight className="w-4 h-4 ml-2" />
                        </a>
                        <a
                            href="mailto:hello@joincastline.com?subject=Castline%20pilot%20%E2%80%94%20can%20we%20jump%20on%20a%20call%3F"
                            className="btn-pill-ghost inline-flex items-center"
                            data-testid="pilot-cta-call"
                        >
                            <Calendar className="w-4 h-4 mr-2" /> Book a 15-min call
                        </a>
                    </div>
                </section>

                {/* VISUAL 1 — dashboard preview */}
                <section data-testid="pilot-visual-1" className="print:hidden">
                    <DashboardPreview />
                </section>

                {/* WHY */}
                <section data-testid="pilot-why">
                    <div className="overline text-coral mb-3">WHY CASTLINE</div>
                    <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-8 max-w-2xl">
                        Keep making content. Make every winner work everywhere.
                    </h2>
                    <div className="grid md:grid-cols-3 gap-4">
                        {VALUE_PROPS.map(({ Icon, title, body, tone }) => (
                            <div
                                key={title}
                                className={`card-ecom p-6 ${tone === "lime" ? "ring-2 ring-lime" : ""}`}
                                style={
                                    tone === "lime"
                                        ? {
                                              background:
                                                  "linear-gradient(180deg, rgba(163,230,53,0.10) 0%, rgba(163,230,53,0.02) 100%)",
                                          }
                                        : undefined
                                }
                            >
                                <div
                                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                                    style={{
                                        background:
                                            tone === "lime"
                                                ? "rgba(163,230,53,0.25)"
                                                : "rgba(255,90,95,0.10)",
                                        color: tone === "lime" ? "#365314" : "#FF5A5F",
                                    }}
                                >
                                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                                </div>
                                <h3 className="font-display text-lg font-semibold mb-1.5">{title}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* HOW */}
                <section data-testid="pilot-how">
                    <div className="overline text-coral mb-3">THE 30-DAY PILOT</div>
                    <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-8">
                        Four steps to creator content running everywhere you spend.
                    </h2>
                    <div className="space-y-4">
                        {STEPS.map(({ n, when, title, body }) => (
                            <div
                                key={n}
                                className="card-ecom p-5 md:p-6 flex flex-col md:flex-row gap-5 md:gap-6"
                                data-testid={`pilot-step-${n}`}
                            >
                                <div className="md:w-32 flex-shrink-0">
                                    <div className="font-display text-3xl font-bold text-coral leading-none">
                                        {n}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider flex items-center gap-1.5">
                                        <Clock className="w-3 h-3" /> {when}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-display text-lg font-semibold mb-1.5">{title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* VISUAL 2 — attribution */}
                <section data-testid="pilot-visual-2" className="print:hidden">
                    <AttributionBar />
                </section>

                {/* GIVE / GET */}
                <section className="grid md:grid-cols-2 gap-4" data-testid="pilot-deal">
                    <div className="card-ecom p-7 surface-warm">
                        <div className="overline text-coral mb-2">YOU GIVE</div>
                        <h3 className="font-display text-xl font-semibold mb-4">Light touch from your team.</h3>
                        <ul className="space-y-2.5">
                            {YOU_GIVE.map((item) => (
                                <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                                    <Check className="w-4 h-4 text-coral mt-0.5 flex-shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div
                        className="card-ecom p-7 relative overflow-hidden"
                        style={{ background: "#0f172a", color: "#fff" }}
                    >
                        <div
                            className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl"
                            style={{ background: "rgba(163,230,53,0.25)" }}
                        />
                        <div className="relative">
                            <div
                                className="inline-block px-2.5 py-0.5 rounded-full mb-2 text-xs font-bold tracking-wider"
                                style={{ background: "#A3E635", color: "#365314" }}
                            >
                                YOU GET
                            </div>
                            <h3 className="font-display text-xl font-semibold mb-4">A real attribution-aware ad system.</h3>
                            <ul className="space-y-2.5">
                                {YOU_GET.map((item) => (
                                    <li key={item} className="flex items-start gap-2 text-sm text-slate-200">
                                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#A3E635" }} />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* PRICING */}
                <section data-testid="pilot-pricing">
                    <div
                        className="card-ecom p-8 lg:p-10"
                        style={{
                            background:
                                "linear-gradient(135deg, rgba(255,90,95,0.06) 0%, rgba(163,230,53,0.10) 100%)",
                        }}
                    >
                        <div className="grid md:grid-cols-3 gap-6 items-center">
                            <div>
                                <div className="overline text-coral mb-2">YOUR COST</div>
                                <div className="font-display text-5xl font-semibold tracking-tight">$0</div>
                                <div className="text-xs text-slate-500 mt-1">platform fee for the pilot</div>
                            </div>
                            <div>
                                <div className="overline text-slate-400 mb-2">CREATOR REV-SHARE</div>
                                <div className="font-display text-3xl font-semibold tracking-tight">
                                    Your call
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                    set bulk default, per-creator or per-video · flat fee or rev-share · paid via Stripe
                                </div>
                            </div>
                            <div>
                                <div className="overline text-slate-400 mb-2">COMMITMENT</div>
                                <div className="font-display text-3xl font-semibold tracking-tight">No contract</div>
                                <div className="text-xs text-slate-500 mt-1">cancel any day · keep your data</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CONTACT */}
                <section
                    className="card-ecom p-8 lg:p-10 text-white relative overflow-hidden"
                    style={{ background: "#0f172a" }}
                    data-testid="pilot-contact"
                >
                    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl bg-coral/30" />
                    <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full blur-3xl bg-lime/20" />
                    <div className="relative">
                        <div
                            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full mb-4 text-xs font-bold tracking-wider"
                            style={{ background: "#A3E635", color: "#365314" }}
                        >
                            <Megaphone className="w-3 h-3" /> HAND-PICKING 3 BRANDS
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-3 max-w-xl">
                            Ready to scale your winners?
                        </h2>
                        <p className="text-slate-300 text-base mb-6 max-w-lg leading-relaxed">
                            If your team works with creators, runs paid social, and wants raw content you can actually edit and ship — let's talk. 15 minutes is all we need to know if it fits.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                            <a
                                href="mailto:hello@joincastline.com?subject=Yes%2C%20count%20us%20in%20for%20the%20Castline%20pilot&body=Hi%20Castline%20team%2C%0A%0AWe%27re%20interested%20in%20joining%20the%20founding%20pilot.%20%20%0A%0ABrand%20name%3A%20%5Byour%20brand%5D%0AContact%20name%3A%20%5Byour%20name%5D%0AWebsite%3A%20%0A%0AHappy%20to%20jump%20on%20a%20call.%0A%0AThanks%21"
                                className="btn-pill-primary inline-flex items-center"
                                data-testid="pilot-cta-final"
                            >
                                Start free pilot <ArrowRight className="w-4 h-4 ml-2" />
                            </a>
                            <a
                                href="mailto:hello@joincastline.com?subject=Castline%20pilot%20%E2%80%94%20can%20we%20jump%20on%20a%20call%3F"
                                className="btn-pill-ghost inline-flex items-center"
                                style={{
                                    background: "transparent",
                                    color: "white",
                                    borderColor: "rgba(255,255,255,0.3)",
                                }}
                            >
                                <Calendar className="w-4 h-4 mr-2" /> Book a 15-min call
                            </a>
                            <a
                                href="mailto:hello@joincastline.com"
                                className="text-sm text-slate-400 hover:text-coral transition-colors inline-flex items-center"
                            >
                                <Mail className="w-4 h-4 mr-1.5" /> hello@joincastline.com
                            </a>
                        </div>
                    </div>
                </section>

                <footer className="border-t hairline pt-6 text-xs text-slate-400 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <DollarSign className="w-3 h-3 text-coral" /> Castline · Get creator content. Use it your way. Pay on performance.
                    </div>
                    <div>joincastline.com · hello@joincastline.com</div>
                </footer>
            </main>
        </div>
    );
}
